
/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/Components/AddKubernetesComponent" />
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { ListSelection } from "VSSUI/List";

import * as Resources from "Environments/ProviderCreateDetails/Kubernetes/Resources";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { AddAzureKubernetesDetailsComponent } from "Environments/ProviderCreateDetails/Kubernetes/Components/AddAzureKubernetesDetailsComponent";
import { IResourceContributionProps, ResourceSectionTypes } from "Environments/EnvironmentsCommon/Utils";
import { StoreManager } from "Environments/Flux/StoreManager";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { KubernetesResourceActionsCreator } from "Environments/ProviderCreateDetails/Kubernetes/ActionsCreators/KubernetesResourceActionsCreator";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { KubernetesResourceStore } from "Environments/ProviderCreateDetails/Kubernetes/Stores/KubernetesResourceStore";
import { IKubernetesProviderData, IAzureKubernetesProviderData, IKubernetesCustomProviderData } from "Environments/ProviderCreateDetails/Kubernetes/Types";
import { PageKey, KubernetesProviderType, KubernetesEndpointAuthorizationType, KubernetesEndpointType, getResourceGroupFromClusterId } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { SubscriptionProvider, SubscriptionConsumer, ISubscriptionContextValue } from "DistributedTask/Subscriptions/SubscriptionPicker";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { ServiceEndpointsActionsCreator } from "Environments/ProviderCreateDetails/Kubernetes/ActionsCreators/ServiceEndpointsActionsCreator";
import { ServiceEndpointsStore } from "Environments/ProviderCreateDetails/Kubernetes/Stores/ServiceEndpointsStore";
import { KubernetesCustomProviderComponent } from "Environments/ProviderCreateDetails/Kubernetes/Components/KubernetesCustomProviderComponent";
import { publishTelemetry, FeatureNames } from "Environments/ProviderCreateDetails/Kubernetes/Telemetry";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { KubernetesResource, EnvironmentResource } from "DistributedTask/Client/Contracts/TaskAgent";
import { MessagesStore } from "Environments/EnvironmentsCommon/MessagesStore";
import { AccessTokenStore } from "Environments/ProviderCreateDetails/Kubernetes/Stores/AccessTokenStore";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { TextField } from "VSSUI/TextField";
import { KubernetesResourceActions } from "Environments/ProviderCreateDetails/Kubernetes/Actions/KubernetesResourceActions";
import { renderGenericKubernetesValidationDialog } from "Environments/ProviderCreateDetails/Kubernetes/Components/GenericKubernetesResourceValidationDialog";

interface IAddKubernetesState extends IEnvironmentBaseComponentState {
    aadTenantId: string | undefined;
    providerData: IKubernetesProviderData;
    existingEndpoint?: ServiceEndpoint;
    creationState: "InProgress" | "Succeeded" | "Failed" | "NotStarted";
    genericResourceValidationType: "Success" | "Warn" | "Error" | "None";
    genericResourceValidationMessage: string;
}

export class AddKubernetesComponent extends EnvironmentBaseComponent<IResourceContributionProps, IAddKubernetesState> {

    public getInstanceId(context: IVssComponentContext): string {
        return "add-kubernetes-instance-id";
    }

    constructor(props: IResourceContributionProps, context: IVssComponentContext) {
        super(props, context);
        let providerData: IKubernetesProviderData = {
            type: this._getDefaultProviderId(),
            environmentName: this.props.environmentName || "",
            createNewEndpoint: true // start with new endpoint flow
        }

        if (props.properties && props.properties.endpoint) {
            providerData = this._getProviderDataFromEndpoint(props.properties.endpoint, providerData);
        }

        this.state = {
            aadTenantId: undefined,
            errorMessages: {},
            providerData: providerData,
            existingEndpoint: props.properties && props.properties.endpoint,
            creationState: "NotStarted",
            genericResourceValidationType: "None",
            genericResourceValidationMessage: ""
        };

        props.footerButtonCallbacks.setForwardButtonText(props.isEditMode ? Resources.ValidateSave : Resources.ValidateCreate);

        this._providerSelection = new ListSelection({ selectOnFocus: false, multiSelect: false });
        this._providerSelection.select(0);
        this._kubernetesResourceActions = ActionsHubManager.GetActionsHub<KubernetesResourceActions>(KubernetesResourceActions, this._instanceId);
        this._kubernetesResourceActionCreator = ActionCreatorManager.CreateActionCreator<KubernetesResourceActionsCreator>(KubernetesResourceActionsCreator, this._instanceId);
        this._serviceEndpointsActionCreator = ActionCreatorManager.CreateActionCreator<ServiceEndpointsActionsCreator>(ServiceEndpointsActionsCreator, this._instanceId);
        this._kubernetesResourceStore = StoreManager.CreateStore<KubernetesResourceStore>(KubernetesResourceStore, this._instanceId);
        this._serviceEndpointsStore = StoreManager.CreateStore<ServiceEndpointsStore>(ServiceEndpointsStore, this._instanceId);
        StoreManager.GetStore<MessagesStore>(MessagesStore, this._instanceId);

        this._kubernetesResourceStore.addChangedListener(this._onKubernetesResourceStoreChange);
        this._serviceEndpointsStore.addChangedListener(this._onServiceEndpointStoreChange);

        this._serviceEndpointsActionCreator.getAADTenantId(this.context.pageContext);
    }

    public render(): React.ReactNode {
        const isHosted = this._isHosted();
        const isInProgress = this.state.creationState === "InProgress";

        if (this.props.isEditMode && !this.state.existingEndpoint) {
            return (<MessageCard
                severity={MessageCardSeverity.Error}
            >
                {Resources.ValidateEndpointNotFound}
            </MessageCard>);
        }

        const getContent = () => (<div className={"k8s-provider flex-grow"}>
            {this.getErrorMessageView(PageKey.AddKubernetesResourceView, "k8s-provider-msg")}
            <FormItem
                label={Resources.ProviderLabel}
                className={"flex-grow k8s-aks-dropdown-form-item"}
            >
                {
                    this.props.isEditMode ?
                        (
                            <TextField
                                value={this.state.providerData.type === KubernetesProviderType.AzureKubernetesServiceProvider ? Resources.AzureKubernetesService : Resources.KubernetesCustomProvider}
                                disabled={true}
                            />)
                        : (
                            <Dropdown
                                items={this._getProviderTypes()}
                                onSelect={this._onProviderTypeChange}
                                selection={this._providerSelection}
                                className="k8s-aks-dropdown"
                                calloutContentClassName={"k8s-aks-dropdown-callout"}
                            />)
                }
            </FormItem>
            {
                this.state.providerData.type === KubernetesProviderType.AzureKubernetesServiceProvider &&
                <AddAzureKubernetesDetailsComponent
                    instanceId={this._instanceId}
                    aadTenantId={this.state.aadTenantId}
                    providerData={this.state.providerData as IAzureKubernetesProviderData}
                    onProviderDataChange={this._onResourceDataChange}
                    redirectUrl={this.props.urlToCurrentView}
                    disableAll={isInProgress || this.props.isEditMode!}
                    isEditMode={this.props.isEditMode}
                    isNewSubscriptionCreated={this._isNewSubscriptionCreated}
                />
            }
            {
                this.state.providerData.type === KubernetesProviderType.CustomProvider &&
                <KubernetesCustomProviderComponent
                    instanceId={this._instanceId}
                    providerData={this.state.providerData as IKubernetesCustomProviderData}
                    onProviderDataChange={this._onResourceDataChange}
                    disableAll={isInProgress}
                    isEditMode={this.props.isEditMode}
                />
            }
        </div>);

        return (
            <ErrorBoundary handlerComponentType="tfs.page-error-handler" >
                <div className={"flex-column flex-grow k8s-panel"}>
                    <h2 className={this.props.headerClassName}>{Resources.KubernetesResourceTitle}</h2>
                    {
                        isHosted ? (
                            <SubscriptionProvider
                                pageContext={this.context.pageContext} >
                                <SubscriptionConsumer>
                                    {
                                        (subscriptionContext: ISubscriptionContextValue) => {
                                            if (!this.state.providerData.type && subscriptionContext.state.loadedAllSubscriptions) {
                                                if (subscriptionContext.state.subscriptions
                                                    && subscriptionContext.state.subscriptions.length > 0) {
                                                    setTimeout((event: any) => {
                                                        this._providerSelection.select(0);
                                                        this._onProviderTypeChange(event, {
                                                            id: KubernetesProviderType.AzureKubernetesServiceProvider,
                                                            text: Resources.AzureKubernetesService
                                                        })
                                                    }, 0);
                                                }
                                            }

                                            return getContent();
                                        }
                                    }
                                </SubscriptionConsumer>
                            </SubscriptionProvider >)
                            : getContent()

                    }

                    {this.state.creationState === "InProgress"
                        && (<Spinner
                            ariaLive={"polite"}
                            size={SpinnerSize.small}
                            className={"flex flex-row create-loading"}
                            label={this.props.isEditMode ? Resources.SavingResource : Resources.CreatingResource}
                        />)
                    }
                </div >
            </ErrorBoundary>
        );
    }

    public componentDidMount(): void {
        publishTelemetry(this.context.pageContext, FeatureNames.AddK8sRes, { provider: (this.state.providerData && this.state.providerData.type) || "unselected" });
    }

    public componentDidUpdate(prevProps: IResourceContributionProps, prevState: IAddKubernetesState) {
        if (this.props.createResourceSignal && prevProps.createResourceSignal !== this.props.createResourceSignal) {
            // This lifecycle command is most likely invoked by a flux action. Set timeout to get out of that code path
            setTimeout(() => {
                if (this.state.providerData.type === "CustomProvider") {
                    this._kubernetesResourceActionCreator.validateGenericEndpoint(this.context.pageContext, this.state.providerData as IKubernetesCustomProviderData);
                }
                else {
                    this._createOrUpdateResource();
                }
            }, 0);
        }

        if (prevState.creationState === "InProgress" && this.state.creationState !== "InProgress") {
            // This lifecycle command is most likely invoked by a flux action. Set timeout to get out of that code path
            this.setTimeout(() => this.props.footerButtonCallbacks.updateButtonVisibility(true), 0);
        }

        if (prevState.creationState !== "InProgress" && this.state.creationState === "InProgress") {
            // This lifecycle command is most likely invoked by a flux action. Set timeout to get out of that code path
            this.setTimeout(() => this.props.footerButtonCallbacks.updateButtonVisibility(false), 0);
        }

        if (prevState.genericResourceValidationType === "None") {
            if (this.state.genericResourceValidationType === "Warn") {
                renderGenericKubernetesValidationDialog(
                    this.context.pageContext,
                    this.state.genericResourceValidationMessage,
                    () => {
                        this._kubernetesResourceActions.genericValidationResult.invoke({ type: "None", message: "" });
                        this._createOrUpdateResource();
                    },
                    () => {
                        this._kubernetesResourceActions.genericValidationResult.invoke({ type: "None", message: "" });
                        this._kubernetesResourceActions.updateCreationState.invoke("NotStarted");
                        this.props.onResourceCreated.onError(Resources.ServiceAccoutResourceCreationAborted);
                    });
            }
            else if (this.state.genericResourceValidationType === "Success") {
                this.setTimeout(() => {
                    this._createOrUpdateResource();
                    this._kubernetesResourceActions.genericValidationResult.invoke({
                        type: "None", message: ""
                    })
                }, 0);
            }
        }
    }

    public componentWillUnmount(): void {
        this._kubernetesResourceStore.removeChangedListener(this._onKubernetesResourceStoreChange);
        this._serviceEndpointsStore.removeChangedListener(this._onServiceEndpointStoreChange);

        StoreManager.DeleteStore<KubernetesResourceStore>(KubernetesResourceStore, this._instanceId);
        StoreManager.DeleteStore<ServiceEndpointsStore>(ServiceEndpointsStore, this._instanceId);

        ActionCreatorManager.DeleteActionCreator<KubernetesResourceActionsCreator>(KubernetesResourceActionsCreator, this._instanceId);
        ActionCreatorManager.DeleteActionCreator<ServiceEndpointsActionsCreator>(ServiceEndpointsActionsCreator, this._instanceId);

        super.componentWillUnmount();
    }

    private _onResourceDataChange = (data: IKubernetesProviderData, isValid: boolean) => {
        this.setState({ providerData: data });
        this.props.footerButtonCallbacks.onCreateButtonStatusUpdate(!isValid);
    }

    private _onProviderTypeChange = (event: any, selectedProvider: IListBoxItem<{}>) => {
        // re-initialize state here
        this.setState({
            providerData: {
                type: selectedProvider.id as "AzureKubernetesServiceProvider" | "CustomProvider" | "",
                createNewEndpoint: true,
                environmentName: this.props.environmentName || ""
            }
        });

        ActionsHubManager.GetActionsHub<MessagesActions>(MessagesActions, this._instanceId).clearMessage.invoke(PageKey.AddKubernetesResourceView);
        publishTelemetry(this.context.pageContext, FeatureNames.AddK8sResProviderChange, { provider: selectedProvider.id as string });
        this.props.footerButtonCallbacks.onCreateButtonStatusUpdate(true);
    }

    private _isNewSubscriptionCreated = (isSubscriptionCreated: boolean): void => {
        this._isSubscriptionCreated = this._isSubscriptionCreated || isSubscriptionCreated;
    }

    private _onKubernetesResourceStoreChange = (): void => {
        const state = this._kubernetesResourceStore.getState();
        if (state.resource && state.creationState === "Succeeded") {
            this.props.onResourceCreated.onSuccess(state.resource);
        }
        else {
            this.setState({
                creationState: state.creationState,
                genericResourceValidationType: state.genericResourceValidationType,
                genericResourceValidationMessage: state.genericResourceValidationMessage
            });
        }
    }

    private _onServiceEndpointStoreChange = (): void => {
        const state = this._serviceEndpointsStore.getState();

        this.setState({
            aadTenantId: state.aadTenantId,
        });
    }

    private _getProviderTypes(): IListBoxItem<{}>[] {
        const isHosted = this._isHosted();

        const providerTypes =
            isHosted ? [
                { id: KubernetesProviderType.AzureKubernetesServiceProvider, text: Resources.AzureKubernetesService }
            ]
                : [];
        providerTypes.push(
            { id: KubernetesProviderType.CustomProvider, text: Resources.KubernetesCustomProvider }
        );

        return providerTypes
    }

    private _getDefaultProviderId(): "AzureKubernetesServiceProvider" | "CustomProvider" {
        const isHosted = this._isHosted();
        return isHosted ? KubernetesProviderType.AzureKubernetesServiceProvider : KubernetesProviderType.CustomProvider;
    }

    private _createOrUpdateResource(): void {
        if (this.props.environmentId) {
            if (this.props.isEditMode && this.props.resource) {
                this._kubernetesResourceActionCreator.UpdateResourceAuth(this.context.pageContext, this.props.resource.id, this.props.environmentId, this.state.providerData, this.state.existingEndpoint!, this.props.onResourceCreated, this.state.aadTenantId, this._getCachedAccessTokens());
            }
            else {
                this._kubernetesResourceActionCreator.createResource(this.context.pageContext, this.state.providerData, this.props.environmentId, this.props.onResourceCreated, this.state.aadTenantId, this._getCachedAccessTokens(), this._isSubscriptionCreated);
            }
        }
    }

    private _isHosted(): boolean {
        return PageContextHelper.isHostedType(this.context.pageContext);
    }


    private _getCachedAccessTokens(): { [key: string]: string } {
        return StoreManager.GetStore<AccessTokenStore>(AccessTokenStore, this.props.instanceId).getState();
    }

    private _getProviderDataFromEndpoint(endpoint: ServiceEndpoint, providerData: IKubernetesProviderData): IKubernetesProviderData {
        const k8sResource = this.props.resource as KubernetesResource
        if (endpoint.data.authorizationType === KubernetesEndpointAuthorizationType.AzureSubscription) {
            const azSubProviderData = providerData as IAzureKubernetesProviderData;
            azSubProviderData.type = KubernetesProviderType.AzureKubernetesServiceProvider;
            azSubProviderData.cluster = { id: endpoint.data.clusterId, name: k8sResource.clusterName, fqdn: endpoint.url, resourceGroup: getResourceGroupFromClusterId(endpoint.data.clusterId) };
            azSubProviderData.subscription = { displayName: endpoint.data.azureSubscriptionName, subscriptionId: endpoint.data.azureSubscriptionId, subscriptionTenantId: endpoint.authorization.parameters.azureTenantId, subscriptionTenantName: "" };
            azSubProviderData.endpointId = endpoint.id;
            azSubProviderData.kubernetesNamespace = endpoint.data.namespace;

            setTimeout(() => this.props.footerButtonCallbacks.onCreateButtonStatusUpdate(false), 0);
        }
        else if (endpoint.data.authorizationType === KubernetesEndpointAuthorizationType.ServiceAccount) {
            const saProviderData = providerData as IKubernetesCustomProviderData;
            saProviderData.type = KubernetesProviderType.CustomProvider;
            saProviderData.endpointId = endpoint.id;
            saProviderData.clusterName = k8sResource.clusterName;
            saProviderData.acceptUntrustedCertificates = endpoint.data.acceptUntrustedCerts === "true";
            saProviderData.kubernetesNamespace = k8sResource.namespace;
            saProviderData.kubernetesServerUrl = endpoint.url;
            saProviderData.serviceAccountName = endpoint.name;
        }

        return providerData;
    }

    private _kubernetesResourceActions: KubernetesResourceActions;
    private _kubernetesResourceActionCreator: KubernetesResourceActionsCreator;
    private _serviceEndpointsActionCreator: ServiceEndpointsActionsCreator;
    private _kubernetesResourceStore: KubernetesResourceStore;
    private _serviceEndpointsStore: ServiceEndpointsStore;
    private _providerSelection: ListSelection;
    private _isSubscriptionCreated: boolean;
}

VssComponent.register(ResourceSectionTypes.Kubernetes, AddKubernetesComponent);